import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Listing, Url } from '@smg-real-estate/hg-rets-schema';
import { OfferType } from '@smg-real-estate/hg-rets-schema/dist/esm/model/HgRetsListing';
import { AgencyBasePackage, ListingType, StorageHelper } from '@smg-real-estate/hg-search-libs';
import {
  convertTreatmentsToTrackingString,
  TreatmentConfig,
} from '@smg-real-estate/vue-experiments';
import { RouteLocation } from 'vue-router';

import { ServiceBoxConfig } from '@/components/ServiceBoxes/helpers/serviceboxes-helpers';
import {
  convertListingTypeToValuesForTracking,
  getBasicPageViewTrackingContext,
} from '@/helpers/GTM/GTMHelper';
import {
  GTMPageFeatureId,
  GTMPageType,
  InteractionActor,
  InteractionContainerDetail,
  InteractionContainerName,
  InteractionElement,
  InteractionEvent,
  InteractionGoal,
  TrackingParameterValue,
} from '@/helpers/GTM/GTMParameters';
import {
  trackEvent,
  trackEventHarmonized,
  trackEventWithoutPreDefinedListing,
  trackVirtualPageView,
} from '@/helpers/GTM/GTMTrack';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import {
  getWelcomeTenantPlusEntryPoint,
  getWelcomeTenantPlusEventLabel,
} from '@/helpers/TenantPlus/TenantPlusModalOpenTriggers';
import { HgLocalStorageKey, NewConstructionDataWithOtherListings } from '@/models';
import { getActiveTreatments } from '@/splitio/trackingHelper';
import { consumeCrossPageAnalyticsPayload } from '@smg-real-estate/seeker-app-shared-components';

const storageHelper = new StorageHelper(StorageNamespace.detailsPage);

export enum LeadType {
  EMAIL = 'email',
  CALL = 'call',
}

function getDetailPageListingTrackingValues(
  listing: Listing,
  listingType: ListingType | undefined,
  newConstructionProject: NewConstructionDataWithOtherListings | null | undefined,
): Record<string, TrackingParameterValue> {
  return {
    listing_propertyReferenceId: listing.externalIds?.propertyReferenceId,
    listing_displayPropertyReferenceId: listing.externalIds?.displayPropertyReferenceId,
    listing_id: listing.id ?? '__MISSING_ID__',
    listing_lister_id: listing.lister?.id,
    listing_valueAddedServices: convertListingTypeToValuesForTracking(listingType),
    listing_zip: listing.address?.postalCode,
    listing_canton: listing.address?.region,
    listing_offerType: listing.offerType,
    listing_categories: (listing.categories ?? []).join(),
    listing_projectId: newConstructionProject?.projectName,
    listing_geo_tags: listing.address?.geoTags?.join(','),
  };
}

export function trackVisitedListing(listing: Listing, storedListingTtl = 30): [string, string][] {
  let visitedListingLocalStorage = storageHelper.getLocalStorageItem(
    HgLocalStorageKey.VISITED_LISTING_IDS,
  );
  // deal with the old structure of listing Ids (plain arrays of ids instead of serialized maps)
  if (!visitedListingLocalStorage || visitedListingLocalStorage[0].length < 2) {
    visitedListingLocalStorage = [];
  }

  // cleanup (listings more than `storedListingTtl` days old)
  const filtered = visitedListingLocalStorage.filter((storedListing: any) => {
    const date = new Date();
    date.setDate(date.getDate() - storedListingTtl);
    return new Date(storedListing[1]) > date;
  });

  const visitedListingIds = new Map<string, string>(filtered).set(
    listing.id ?? '__MISSING_ID__',
    new Date().toISOString(),
  );
  const serializedVisitedListings = Array.from(visitedListingIds);
  storageHelper.setLocalStorageItem(
    HgLocalStorageKey.VISITED_LISTING_IDS,
    serializedVisitedListings,
  );
  return serializedVisitedListings;
}

/* eslint-disable-next-line max-params */
export function trackDetailPageVirtualPageView(
  language: HgSupportedLanguage,
  listing: Listing,
  listingType: ListingType | undefined,
  listingPicturesCount: number,
  newConstructionProject: NewConstructionDataWithOtherListings | null,
  loginStatusTrackingParameters: Record<string, string | undefined> | undefined = undefined,
  treatments: Record<string, TreatmentConfig> = {},
  serviceBoxes: ServiceBoxConfig[],
  basePackage: AgencyBasePackage | null | undefined,
  shouldShowAgencyCard: boolean,
): void {
  const crossPagePayload = consumeCrossPageAnalyticsPayload();

  const funnelPayload: Record<string, TrackingParameterValue> = {};

  if (typeof crossPagePayload?.searchType === 'string') {
    funnelPayload.h_funnel_step_input_141 = crossPagePayload.searchType;
  }
  if (typeof crossPagePayload?.featureGroup === 'string') {
    funnelPayload.h_feature_group_001 = crossPagePayload.featureGroup;
  }
  if (typeof crossPagePayload?.semanticSearchInput === 'string') {
    funnelPayload.h_search_term_input_081 = crossPagePayload.semanticSearchInput;
  }
  if (typeof crossPagePayload?.nlsMessageId === 'string') {
    funnelPayload.h_ai_result_id_020 = crossPagePayload.nlsMessageId;
  }

  trackVisitedListing(listing);

  trackEventHarmonized(InteractionEvent.VIRTUAL_PAGEVIEW, {
    h_client_login_provider_034: loginStatusTrackingParameters?.client_login_provider,
    h_login_status_035: loginStatusTrackingParameters?.client_login_status,
    h_client_login_user_id_036: loginStatusTrackingParameters?.h_client_login_user_id_036,
    h_login_user_type_037: loginStatusTrackingParameters?.h_login_user_type_037,
    h_experiment_split_treatments_107: convertTreatmentsToTrackingString(
      getActiveTreatments(treatments),
    ),
    h_page_type_110: GTMPageType.DETAIL_PAGE,
    h_content_language_116: language,
    h_listing_offer_type_117: listing.offerType,
    h_listing_categories_hgrets_118: (listing.categories ?? []).join(),
    h_listing_advertisement_id_120: listing.id ?? '__MISSING_ID__',
    h_listing_object_ref_id_123: listing.externalIds?.propertyReferenceId,
    h_listing_geo_zip_152: listing.address?.postalCode,
    h_listing_lister_id_154: listing.lister?.id,
    h_listing_picture_count_155: listingPicturesCount,
    h_listing_vas_156: listing.valueAddedServices?.isTenantPlusListing
      ? `tenantPlus!${listingType}`
      : listingType,
    h_listing_geo_canton_157: listing.address?.region,
    h_listing_geo_country_158: listing.address?.country,
    h_listing_project_id_159: newConstructionProject?.projectName,
    h_is_tenant_plus_user_166: loginStatusTrackingParameters?.h_is_tenant_plus_user_166,
    h_listing_price_gross_303:
      listing.offerType === OfferType.Rent
        ? listing.prices?.rent?.gross
        : listing.prices?.buy?.price,
    h_features_displayed_171: serviceBoxes.map((serviceBox) => serviceBox.eventName).join('&'),
    h_listing_package_name_108: basePackage ?? undefined,
    h_list_item_agency_pic_176: shouldShowAgencyCard ? 1 : 0,
    ...funnelPayload,
  });
}

export function trackListingNotFoundVirtualPageView(
  fullRoutePath: string,
  normalizedFullRoutePath: string,
  language: HgSupportedLanguage,
  route: RouteLocation,
): void {
  const trackingContext = getBasicPageViewTrackingContext(
    GTMPageType.PAGE_NOT_FOUND,
    language,
    fullRoutePath,
    normalizedFullRoutePath,
  );
  trackVirtualPageView({
    ...trackingContext,
    h_web_feature_id_019: GTMPageFeatureId.DETAIL_PAGE_AD_NOT_FOUND,
    tech_statusCode: 'adNotFound',
    listing_id: route.params.id,
    page_targetUrl: route.fullPath,
  });
}

export function trackOutboundLink(
  listing: Listing,
  listingType: ListingType | undefined,
  newConstructionProject: NewConstructionDataWithOtherListings | null | undefined,
  externalWebsiteLink: Url,
): void {
  const trackingValues = getDetailPageListingTrackingValues(
    listing,
    listingType,
    newConstructionProject,
  );
  trackEvent(InteractionEvent.OUTBOUND_LINK, {
    ...trackingValues,
    h_web_feature_id_019: GTMPageFeatureId.OUTBOUND_LINK_CLICKED,
    interaction_goal: InteractionGoal.LISTING_OUTBOUND_LINK,
    page_title: externalWebsiteLink.title,
    page_targetUrl: externalWebsiteLink.value,
  });
}

export function trackShareOpen(listingId: string, sessionId: string): void {
  trackEventHarmonized(InteractionEvent.OPEN_SHARE, {
    h_event_category_006: InteractionEvent.SHARE_OPEN,
    h_event_action_007: InteractionGoal.SHARE_SEE_OPTIONS,
    h_event_label_008: listingId,
    h_share_id_070: sessionId,
  });
}

export function trackShareAction(
  listingId: string,
  sessionId: string,
  channel: InteractionGoal,
): void {
  trackEventHarmonized(InteractionEvent.SHARE_ACTION, {
    h_event_category_006: InteractionEvent.SHARE_ACTION,
    h_event_action_007: channel,
    h_event_label_008: listingId,
    h_share_id_070: sessionId,
  });
}
export function trackOpenPrint(): void {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_PRINT,
    h_event_label_008: undefined,
  });
}

export function trackPartnerLinkExtended({
  listing,
  externalLink,
  goal,
  containerDetail,
  interactionElement,
  eventParams,
}: {
  listing: Listing;
  listingType: ListingType;
  externalLink: Url;
  goal: InteractionGoal;
  containerDetail?: InteractionContainerDetail;
  interactionElement?: InteractionElement;
  eventParams?: Record<string, TrackingParameterValue>;
}): void {
  trackEventWithoutPreDefinedListing(InteractionEvent.PARTNER_LINK, {
    h_web_feature_id_019: GTMPageFeatureId.DETAIL_PAGE_PARTNER_LINK_CLICKED,
    interaction_containerName: InteractionContainerName.PDP_CONTENT,
    interaction_containerDetail: containerDetail,
    interaction_goal: goal,
    interaction_element: interactionElement,
    listing_id: listing.id ?? '__MISSING_ID__',
    listing_offerType: listing.offerType,
    page_title: externalLink.title,
    page_targetUrl: externalLink.value,
    ...eventParams,
  });
}

export function trackWincasaViewingLinkClick(externalUrl: string): void {
  trackEventHarmonized(InteractionEvent.PARTNER_LINK, {
    h_event_category_006: InteractionEvent.PARTNER_LINK,
    h_event_action_007: InteractionGoal.GO_TO_PARTNER_SITE,
    h_event_label_008: 'mywincasa',
    h_target_url_029: externalUrl,
  });
}

export function trackPartnerWidget(containerDetail: InteractionContainerDetail): void {
  trackEventWithoutPreDefinedListing(InteractionEvent.FEATURE_DISPLAYED, {
    h_web_feature_id_019: GTMPageFeatureId.PARTNER_WIDGET,
    interaction_goal: InteractionGoal.DISPLAY_FEATURE,
    interaction_containerDetail: containerDetail,
    interaction_element: InteractionElement.WIDGET,
    interaction_actor: InteractionActor.SYSTEM,
  });
}

export function trackFraudReportAction(containerName: InteractionContainerName): void {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.REPORT_LISTING,
    h_event_label_008: containerName,
  });
}

export function trackFraudReportSubmit(containerDetail: InteractionContainerDetail): void {
  trackEventHarmonized(InteractionEvent.BUTTON_CLICK, {
    h_event_category_006: InteractionEvent.BUTTON_CLICK,
    h_event_action_007: InteractionGoal.SEND_REPORT,
    h_event_label_008: containerDetail,
  });
}
export function trackListingAccessoryClick(interactionGoal: InteractionGoal): void {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_action_007: interactionGoal,
    h_event_label_008: InteractionContainerName.LISTING_ACCESSORIES_LINKS,
  });
}

export function trackBottomSheetDisplayed(bottomSheetType: string): void {
  trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
    h_event_category_006: InteractionEvent.FEATURE_DISPLAYED,
    h_event_action_007: InteractionGoal.DISPLAY_FEATURE,
    h_event_label_008: bottomSheetType,
    h_interaction_element_055: InteractionElement.MODAL,
  });
}

export function trackBreadCrumbClicked(eventLabel: string): void {
  trackEventHarmonized('generic_link_click', {
    h_event_category_006: 'generic_link_click',
    h_event_action_007: 'breadcrumbs',
    h_event_label_008: eventLabel,
  });
}

export function trackWelcomeTenantPlusSubscription(isTenantPlusSubscriber: boolean): void {
  if (!isTenantPlusSubscriber) {
    return;
  }

  const funnelStepInput = getWelcomeTenantPlusEntryPoint();
  const eventLabel = getWelcomeTenantPlusEventLabel();

  trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
    h_event_category_006: InteractionEvent.FEATURE_DISPLAYED,
    h_event_action_007: InteractionGoal.WELCOME_TPLUS_SUBSCRIPTION,
    ...(eventLabel ? { h_event_label_008: eventLabel } : {}),
    ...(funnelStepInput ? { h_funnel_step_input_141: funnelStepInput } : {}),
  });
}
